<template>
  <div v-click-out="handleClickOutside" class="p-text-input" :size="size" @click="focusInput(name)">
    <div class="p-text-input__cover" :error="errorState || error" :inputDisabled="disabled">
      <span v-if="$slots.prepend" class="p-text-input__prepend">
        <slot name="prepend" />
      </span>
      <div class="p-text-input__inner">
        <input
          :id="name"
          v-model="value"
          v-bind="{ ...$attrs, ...(props as InputHTMLAttributes) }"
          class="p-text-input__field"
          :data-test="testAttribute"
          :autocomplete="autocomplete"
          required
          :maxlength="maxLength"
          @focus="handleFocus"
          @blur="handleBlur(value)"
          @input="onInput"
          @change="$emit('on-change', value)"
        />
        <label
          :for="name"
          class="p-text-input__label"
          :defaultPosition="!value && !textInputState.onFocus ? true : false"
          v-text="label"
        />
      </div>
      <span v-if="$slots.append" class="p-text-input__append">
        <slot name="append" />
      </span>
      <span v-if="$slots.appendAnimation" class="p-text-input__append--animation">
        <slot name="appendAnimation" />
      </span>
      <template v-if="maxLength && showCounter">
        <span class="f-body-12 t-fg-tertiary p-text-input__append pr-8">
          <span v-text="String(value ?? '').length" />
          /
          <span v-text="maxLength" />
        </span>
      </template>
    </div>
    <p-error-message v-if="errorState && !disabled" :name="name" class="p-text-input--error" />
  </div>
</template>

<script setup lang="ts">
import type { InputHTMLAttributes } from 'vue'
import { useField, ErrorMessage as PErrorMessage } from 'vee-validate'

const emit = defineEmits<{
  'on-change': [value: any]
  'on-input': [value: any]
  'on-focus': [value: boolean]
  'on-blur': [value: string | number]
  'v-out-input': [value: any]
}>()

interface TextInputState {
  onFocus: boolean
  onBlur: boolean
}

const textInputState = reactive<TextInputState>({
  onFocus: false,
  onBlur: false,
})
const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: null,
  },
  maxLength: {
    type: Number,
    default: null,
  },
  type: {
    type: String,
    default: 'text',
  },
  step: {
    type: [String, Number],
    default: 1,
  },
  inputmode: {
    type: String,
    default: 'text',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  validateOnBlur: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String,
    default: 'md',
  },
  autocomplete: {
    type: String,
    default: 'off',
  },
  error: {
    type: Boolean,
    default: false,
  },
  showCounter: {
    type: Boolean,
    default: true,
  },
})

const testAttribute = dataTest(props.name as string)
const name = toRef(props, 'name')
const modelValue = toRef(props, 'modelValue')

const { value, meta, validate } = useField(name, undefined, {
  initialValue: modelValue.value,
})

const errorState = computed<boolean>(() => {
  return !meta.valid && meta.validated
})

const focusInput = (name: string) => {
  const el = document.getElementById(name) as HTMLElement
  el.focus()
}

const handleFocus = () => {
  textInputState.onFocus = true
  textInputState.onBlur = false
  emit('on-focus', textInputState.onFocus)
}

const handleBlur = async (value: string | number) => {
  textInputState.onBlur = true
  textInputState.onFocus = false
  if (props.validateOnBlur) {
    await validate()
  }
  emit('on-blur', value)
}

const onInput = (e: Event) => {
  value.value = (e.target as HTMLInputElement).value
  emit('on-input', value.value)
}

const handleClickOutside = (e: Event | null) => {
  // The v-click-out directive passes the original Event; previously typed
  // as EventTarget which has no `.target`.
  const target = (e?.target as Node | null) ?? null
  if (target && document.body.contains(target)) emit('v-out-input', value.value)
}

watch(
  () => modelValue.value,
  () => (value.value = modelValue.value),
)
</script>
